import {
  InstitutionFieldRow,
  RequiredFields,
  StudentFieldRow
} from './models/transcriptExtractionData.model';

export const BatchStatus = {
  Available: 'available',
  CheckedOut: 'checked_out',
  Completed: 'completed',
  Deleted: 'deleted',
  Draft: 'draft'
};

export enum TranscriptStatus {
  NotStarted = 0,
  PendingExtraction = 1,
  Extracting = 2,
  ReadyForExport = 3,
  Failed = 4,
  Archived = 5,
  Exported = 6,
  Assigned = 7,
  NeedsReview = 8,
  FailedToExport = 9,
  // FailedExtraction = 10, this status isn't used in the codebase yet but is in the DB.
  FailedMatching = 11,
  PartiallyExported = 12
}

export const TranscriptStatusLabels: Record<TranscriptStatus, string> = {
  [TranscriptStatus.NotStarted]: 'Not Started',
  [TranscriptStatus.PendingExtraction]: 'Pending Extraction',
  [TranscriptStatus.Extracting]: 'Extracting',
  [TranscriptStatus.ReadyForExport]: 'Ready for Export',
  [TranscriptStatus.Failed]: 'Failed',
  [TranscriptStatus.Archived]: 'Archived',
  [TranscriptStatus.Exported]: 'Exported',
  [TranscriptStatus.Assigned]: 'Assigned',
  [TranscriptStatus.NeedsReview]: 'Needs Review',
  [TranscriptStatus.FailedToExport]: 'Failed to Export',
  [TranscriptStatus.FailedMatching]: 'Failed Matching',
  [TranscriptStatus.PartiallyExported]: 'Partially Exported'
};

export enum TranscriptType {
  College = 0,
  HighSchool = 1
}

export const TranscriptTypeLabels: Record<TranscriptType, string> = {
  [TranscriptType.HighSchool]: 'High School',
  [TranscriptType.College]: 'College'
};

// Validator UI elements to show
export enum ValidateElementType {
  Asterisk = 1,
  Text = 2,
  Icon = 3
}

export const StudentFieldRows: StudentFieldRow[][] = [
  [
    { id: 'firstName', fieldKey: 'firstName', modelKey: 'FirstName' },
    { id: 'middleName', fieldKey: 'middleName', modelKey: 'MiddleName' },
    { id: 'lastName', fieldKey: 'lastName', modelKey: 'LastName' }
  ],
  [
    { id: 'dateOfBirth', fieldKey: 'dateOfBirth', modelKey: 'DateOfBirth' },
    { id: 'last4SSN', fieldKey: 'last4SSN', modelKey: 'Last4SSN' },
    { id: 'email', fieldKey: 'email', modelKey: 'Email' },
    { id: 'studentId', fieldKey: 'studentId', modelKey: 'StudentId' }
  ]
];

export const CollegeInstitutionRows: InstitutionFieldRow[][] = [
  [
    { id: 'institutionName', fieldKey: 'institutionName', modelKey: 'Name' },
    { id: 'totalCredits', fieldKey: 'totalCreditsEarned', modelKey: 'TotalCreditsEarned' },
    { id: 'cumulativeGPA', fieldKey: 'cumulativeGPA', modelKey: 'CumulativeGPA' }
  ]
];

export const HighschoolInstitutionRows: InstitutionFieldRow[][] = [
  [
    { id: 'institutionName', fieldKey: 'institutionName', modelKey: 'Name' },
    { id: 'ceeb', fieldKey: 'ceeb', modelKey: 'CEEB' },
    { id: 'totalCredits', fieldKey: 'totalCreditsEarned', modelKey: 'TotalCreditsEarned' }
  ],
  [
    { id: 'weightedGPA', fieldKey: 'weightedGPA', modelKey: 'WeightedGPA' },
    { id: 'unweightedGPA', fieldKey: 'unweightedGPA', modelKey: 'UnweightedGPA' },
    { id: 'gpa', fieldKey: 'gpa', modelKey: 'GPA' }
  ],
  [
    { id: 'attendanceStartDate', fieldKey: 'attendanceStartDate', modelKey: 'AttendanceStartDate' },
    { id: 'attendanceEndDate', fieldKey: 'attendanceEndDate', modelKey: 'AttendanceEndDate' }
  ],
  [
    { id: 'attendanceStartYear', fieldKey: 'attendanceStartYear', modelKey: 'AttendanceStartYear' },
    { id: 'attendanceEndYear', fieldKey: 'attendanceEndYear', modelKey: 'AttendanceEndYear' }
  ],
  [
    { id: 'graduationDate', fieldKey: 'graduationDate', modelKey: 'GraduationDate' },
    { id: 'graduationStatus', fieldKey: 'graduationStatus', modelKey: 'GraduationStatus' }
  ],
  [
    { id: 'classRank', fieldKey: 'classRank', modelKey: 'ClassRank' },
    { id: 'classSize', fieldKey: 'classSize', modelKey: 'ClassSize' }
  ]
];

export const InstitutionMap = new Map([
  [TranscriptType.College, CollegeInstitutionRows],
  [TranscriptType.HighSchool, HighschoolInstitutionRows]
]);

export const TranscriptRequiredFields: RequiredFields = {
  student: [],
  course: [],
  institution: []
};

export interface StatusConfigEntry {
  text: string;
  translationKey: string;
  cssClass: string;
}

export const StatusConfig = new Map<number, StatusConfigEntry>([
  [
    TranscriptStatus.PendingExtraction,
    {
      text: 'Processing',
      translationKey: 'transcriptData.pendingExtractionStatus',
      cssClass: 'status-default'
    }
  ],
  [
    TranscriptStatus.Extracting,
    {
      text: 'Processing',
      translationKey: 'transcriptData.extractingStatus',
      cssClass: 'status-default'
    }
  ],
  [
    TranscriptStatus.ReadyForExport,
    {
      text: 'Ready for Export',
      translationKey: 'transcriptData.readyForExportStatus',
      cssClass: 'status-ready-for-export'
    }
  ],
  [
    TranscriptStatus.NeedsReview,
    {
      text: 'Needs Review',
      translationKey: 'transcriptData.needsReviewStatus',
      cssClass: 'status-needs-review'
    }
  ],
  [
    TranscriptStatus.Failed,
    {
      text: 'Processing',
      translationKey: 'transcriptData.failedStatus',
      cssClass: 'status-default'
    }
  ],
  [
    TranscriptStatus.Assigned,
    {
      text: 'Processing',
      translationKey: 'transcriptData.assignedStatus',
      cssClass: 'status-default'
    }
  ],
  [
    TranscriptStatus.Exported,
    {
      text: 'Exported',
      translationKey: 'transcriptData.exportedStatus',
      cssClass: 'status-exported'
    }
  ],
  [
    TranscriptStatus.Archived,
    {
      text: 'Archived',
      translationKey: 'transcriptData.archivedStatus',
      cssClass: 'status-archived'
    }
  ],
  [
    TranscriptStatus.FailedToExport,
    {
      text: 'Failed to Export',
      translationKey: 'transcriptData.failedToExport',
      cssClass: 'status-failed-to-export'
    }
  ],
  [
    TranscriptStatus.FailedMatching,
    {
      text: 'Failed Matching',
      translationKey: 'transcriptData.failedMatching',
      cssClass: 'status-failed-to-export'
    }
  ],
  [
    TranscriptStatus.PartiallyExported,
    {
      text: 'Partially Exported',
      translationKey: 'transcriptData.partiallyExported',
      cssClass: 'status-needs-review'
    }
  ]
]);

export const REVIEW_CONFIDENCE_THRESHOLD = 80;
